import { RepositoryFactory } from "@/repositories/RepositoryFactory";

const ClassesRepository = RepositoryFactory.get("classes");

const state = {
  classes: [],
  teachers: [],
  class: {},
};

const getters = {};

const actions = {
  createClass({ dispatch, state }, myClass) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return ClassesRepository.createClass(myClass)
      .then((response) => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.class.creationSuccess`),
          },
          { root: true },
        );

        return response;
      })
      .catch((error) => {
        let message = "";

        if (error.response.status == 400) {
          message = state.$t(`message.notifications.class.creationFailure400`);
        } else if (error.response.status == 409) {
          message = state.$t(`message.notifications.class.creationFailure409`);
        } else {
          message = state.$t(`message.notifications.class.creationFailure`);
        }

        dispatch(
          "notifications/addError",
          {
            message: message,
          },
          { root: true },
        );

        throw error;
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  getClasses({ dispatch, commit }, params = {}) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    ClassesRepository.getClasses(params)
      .then((response) => {
        if (params.version == 1) {
          const classes = response.data.results;
          const totalPages = response.data.total_pages;
          commit("pagination/SET_TOTAL_PAGES", totalPages, { root: true });
          commit("SAVE_CLASSES", classes);
        } else {
          const classes = response.data;
          commit("SAVE_CLASSES", classes);
        }
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.class.getClassesError`),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  getTeachers({ dispatch, commit }) {
    ClassesRepository.getTeachers()
      .then((response) => {
        const teachers = response.data;
        commit("SAVE_TEACHERS", teachers);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.teachers.retrieveTeachersError`,
            ),
          },
          { root: true },
        );
      });
  },
  getClass({ dispatch, commit }, classId) {
    return ClassesRepository.getClass(classId)
      .then((response) => {
        const myClass = response.data;
        commit("SAVE_CLASS", myClass);
        return response;
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.class.getClassError`),
          },
          { root: true },
        );
      });
  },
  /**
   * Get class data with, among other things, homework data for the class students.
   * Homework data are paginated.
   */
  getClassWithHomeworks({ dispatch, state, commit }, params) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    let classId = params.classId;
    delete params.classId;

    return ClassesRepository.getClassWithHomeworks(classId, params)
      .then((response) => {
        const myClass = response.data.data;
        const totalPages = response.data.homeworks_total_pages;
        const totalCount = response.data.homeworks_count;

        // if loading new class, save class data
        if (classId !== state.class.id) {
          // sort both students list and homeworks students marks lists the same way
          myClass.students.sort((a, b) => {
            if (a.user.last_name != b.user.last_name) {
              return a.user.last_name.localeCompare(b.user.last_name);
            } else {
              return a.user.first_name.localeCompare(b.user.first_name);
            }
          });

          for (let i = 0; i < myClass.homeworks.length; i++) {
            myClass.homeworks[i].students_marks.sort((a, b) => {
              if (a.student.last_name != b.student.last_name) {
                return a.student.last_name.localeCompare(b.student.last_name);
              } else {
                return a.student.first_name.localeCompare(b.student.first_name);
              }
            });
          }

          commit("SAVE_CLASS", myClass);
        } else {
          // if loading more homeworks for this class
          let homeworks = myClass["homeworks"];

          for (let i = 0; i < homeworks.length; i++) {
            homeworks[i].students_marks.sort((a, b) => {
              if (a.student.last_name != b.student.last_name) {
                return a.student.last_name.localeCompare(b.student.last_name);
              } else {
                return a.student.first_name.localeCompare(b.student.first_name);
              }
            });
          }

          commit("ADD_HOMEWORKS_TO_CLASS", homeworks);
        }

        commit("pagination/SET_TOTAL_PAGES", totalPages, {
          root: true,
        });
        commit("pagination/SET_TOTAL_COUNT", totalCount, {
          root: true,
        });

        return response;
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.class.getClassError`),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  editClass({ dispatch, state }, myClass) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    if (myClass.school.name === "") {
      delete myClass.school;
    }
    return ClassesRepository.editClass(myClass.class_id, myClass)
      .then(() => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.class.creationSuccess`),
          },
          { root: true },
        );
      })
      .catch((error) => {
        let message = "";

        if (error.response.status == 400) {
          message = state.$t(`message.notifications.class.editionFailure400`);
        } else {
          message = state.$t(`message.notifications.class.editionFailure`);
        }

        dispatch(
          "notifications/addError",
          {
            message: message,
          },
          { root: true },
        );

        throw error;
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  deleteStudentClass({ state }, studentId) {
    return ClassesRepository.deleteStudentClass(state.class.id, studentId);
  },
  deleteClass({ dispatch, state }, classId) {
    return ClassesRepository.deleteClass(classId)
      .then(() => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.class.deletionSuccess`),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.class.deletionFailure`),
          },
          { root: true },
        );
      });
  },
  teacherJoinsClass({ dispatch }, classId) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    // Loading disabled in view callback
    return ClassesRepository.teacherJoinsClass(classId);
  },
  /**
   * Update current class students homeworks data
   * @param {Array} studentsHomeworkUpdateData contains objects with keys: homework_id | student_id | mark
   */
  updateClassStudentHomeworkData(
    { commit, state },
    studentsHomeworkUpdateData,
  ) {
    let currentClass = state.class;

    if (Object.keys(currentClass).length > 0) {
      for (let shud of studentsHomeworkUpdateData) {
        let i = currentClass.homeworks.findIndex(
          (h) => h.id == shud.homework_id,
        );
        if (i !== -1) {
          let j = currentClass.homeworks[i].students_marks.findIndex(
            (sm) => sm.student.id == shud.student_id,
          );
          if (j !== -1) {
            currentClass.homeworks[i].students_marks[j].mark = shud.mark;
            currentClass.homeworks[i].students_marks[j].status = "";
          }
        }
      }

      commit("SAVE_CLASS", currentClass);
    }
  },
  resetClasses({ commit }) {
    commit("RESET_CLASSES");
  },
  resetClass({ commit }) {
    commit("RESET_CLASS");
  },
};

const mutations = {
  RESET_CLASSES(state) {
    state.classes = [];
  },
  RESET_CLASS(state) {
    state.class = {};
  },
  SAVE_CLASSES(state, classes) {
    state.classes = classes;
  },
  SAVE_TEACHERS(state, teachers) {
    state.teachers = teachers;
  },
  SAVE_CLASS(state, myClass) {
    state.class = myClass;
  },
  ADD_HOMEWORKS_TO_CLASS(state, homeworks) {
    for (let h of homeworks) {
      // security check: avoid duplicates
      if (
        "homeworks" in state.class &&
        state.class.homeworks.findIndex((item) => item.id === h.id) === -1
      )
        state.class.homeworks.push(h);
    }
  },
};

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
